/**
 * 导出（M2-19 / DESIGN.md §6.5「导出为（多尺寸/多格式一次导出）」）。
 *
 * 管线：CPU 合成压平 → 每个勾选倍率各做一次重采样（缩小走 wasm Lanczos-3，
 * 内含盒式预降采样，§5.3）→ wasm 编码（libpng/mozjpeg，M1-14）→ FileGateway
 * 落盘（FSA 另存为 / 降级环境触发下载，M1-16）。
 *
 * JPEG 没有 alpha：先把透明区合成到白底再编码——静默丢 alpha 会把
 * 透明区变成黑色，这是用户最常抱怨的导出 bug。
 */
import { compositeDocument } from './composite';
import { buildPsdInput } from './export-psd';
import { encodeImage } from '../io/codecs';
import { buildIndexedImage } from '../io/codecs/palette';
import { encodeIndexedPng } from '../io/codecs/png-indexed';
import { throwIfAborted, type FileGateway } from '../io/gateway';
import { resample_lanczos3_rgba8 } from '../wasm/core/photosmith_core';
import { ensureKernel } from '../engine/wasm-kernel';
import type { AppStore } from './store';

/**
 * `png8` = 调色板索引色（参照 Photoshop「存储为 Web 所用格式」的 PNG-8），
 * 不走 wasm 编码器。`psd` 保留图层结构（ADR-0014），不支持缩放批量导出——
 * `exportDocument` 里那条分支会忽略 `scales`，只出一份原始尺寸的文件。
 */
export type ExportFormat = 'png' | 'jpeg' | 'png8' | 'psd';

/** 手动输入的像素宽高——跟 `scales` 等比倍率是两条独立的路径，允许不保持原图宽高比。 */
export interface ExportCustomSize {
  readonly width: number;
  readonly height: number;
}

export interface ExportSpec {
  readonly format: ExportFormat;
  /** JPEG 质量 1–100。PNG/PNG-8 忽略。 */
  readonly quality: number;
  /** 导出倍率（0.5 / 1 / 2…），每个产出一个文件。 */
  readonly scales: readonly number[];
  /** 手动输入的像素宽高，每个产出一个文件。省略 = 无。不要求跟原图等比。 */
  readonly customSizes?: readonly ExportCustomSize[];
  /** 文件基名（不含扩展名）。 */
  readonly baseName: string;
  /** png8 专用：调色板颜色数 2–256。省略 = 256。 */
  readonly colors?: number;
  /** png8 专用：Floyd–Steinberg 误差扩散仿色。省略 = 关。 */
  readonly dither?: boolean;
}

export type ExportEncoder = (
  format: 'png' | 'jpeg',
  pixels: Uint8Array,
  options: { width: number; height: number; quality: number },
) => Promise<Uint8Array>;

export type ExportResampler = (
  pixels: Uint8Array,
  sw: number,
  sh: number,
  dw: number,
  dh: number,
) => Promise<Uint8Array>;

export interface ExportDeps {
  readonly store: AppStore;
  readonly gateway: FileGateway;
  /** 注入点：测试给 node 侧的直连 wasm 实现。 */
  readonly encode?: ExportEncoder | undefined;
  readonly resample?: ExportResampler | undefined;
}

const defaultEncoder: ExportEncoder = async (format, pixels, options) =>
  encodeImage(format, pixels, options);

const defaultResampler: ExportResampler = async (pixels, sw, sh, dw, dh) => {
  await ensureKernel();
  return resample_lanczos3_rgba8(pixels, sw, sh, dw, dh);
};

/** JPEG 前置：透明像素合到白底（直通 alpha → 不透明 RGB）。 */
export function flattenToWhite(pixels: Uint8Array): Uint8Array {
  const out = new Uint8Array(pixels.length);
  for (let i = 0; i < pixels.length; i += 4) {
    const a = pixels[i + 3]! / 255;
    out[i] = Math.round(pixels[i]! * a + 255 * (1 - a));
    out[i + 1] = Math.round(pixels[i + 1]! * a + 255 * (1 - a));
    out[i + 2] = Math.round(pixels[i + 2]! * a + 255 * (1 - a));
    out[i + 3] = 255;
  }
  return out;
}

export interface ExportedFile {
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
  /** 仅 psd：被降级栅格化处理的图层说明（ADR-0014），非空时应该提示用户。 */
  readonly rasterizedLayers?: readonly string[];
}

/** 执行导出。返回产出的文件清单（名称/尺寸/字节数）。 */
export async function exportDocument(
  deps: ExportDeps,
  spec: ExportSpec,
  options: { signal?: AbortSignal } = {},
): Promise<ExportedFile[]> {
  throwIfAborted(options.signal);
  const document = deps.store.document;
  if (document === null) return [];

  // psd 保留图层结构，缩放批量导出的概念不适用——不摊平合成、不走下面
  // 按 scale 循环重采样这条路径，只出一份原始尺寸的文件。`ag-psd`
  // 动态 import：只有真的导出 PSD 才付这份 JS 体积，不拖累主 bundle
  // （所有用户，不管有没有用过 PSD 导出，都会下载主 bundle）
  if (spec.format === 'psd') {
    const { input, rasterizedLayers } = buildPsdInput(document, deps.store.tiles);
    const { encodePsdBytes } = await import('../io/codecs/psd');
    const bytes = await encodePsdBytes(input);
    throwIfAborted(options.signal);
    const name = `${spec.baseName}.psd`;
    await deps.gateway.save(
      { name, bytes, mimeType: 'image/vnd.adobe.photoshop' },
      null,
      options,
    );
    return [
      {
        name,
        width: input.width,
        height: input.height,
        bytes: bytes.length,
        ...(rasterizedLayers.length > 0 ? { rasterizedLayers } : {}),
      },
    ];
  }

  const customSizes = spec.customSizes ?? [];
  if (spec.scales.length === 0 && customSizes.length === 0) return [];

  const flat = compositeDocument(document, deps.store.tiles);
  const encode = deps.encode ?? defaultEncoder;
  const resample = deps.resample ?? defaultResampler;
  const extension = spec.format === 'jpeg' ? 'jpg' : 'png';
  const results: ExportedFile[] = [];

  // 等比倍率（scales）与手动宽高（customSizes）是两条独立的输入路径，但落到
  // 同一条重采样/编码流水线——customSizes 不要求跟原图等比，`resample`
  // 本身早就支持任意 dw/dh（Lanczos 重采样），只是原来只有 scales 这一条
  // 路径在用它，这里只是多喂一组目标尺寸进去，不改流水线本身。
  const targets = [
    ...spec.scales.map((scale) => ({
      width: Math.max(1, Math.round(flat.width * scale)),
      height: Math.max(1, Math.round(flat.height * scale)),
      suffix: scale === 1 ? '' : `@${scale}x`,
    })),
    ...customSizes.map(({ width, height }) => {
      const w = Math.max(1, Math.round(width));
      const h = Math.max(1, Math.round(height));
      return { width: w, height: h, suffix: `_${w}x${h}` };
    }),
  ];

  for (const { width, height, suffix } of targets) {
    throwIfAborted(options.signal);
    let pixels =
      width === flat.width && height === flat.height
        ? flat.pixels
        : await resample(flat.pixels, flat.width, flat.height, width, height);
    if (spec.format === 'jpeg') pixels = flattenToWhite(pixels);
    throwIfAborted(options.signal);

    // png8 走纯 TS 的调色板量化 + 编码（palette.ts / png-indexed.ts），
    // 不经过 wasm 编码器——没有"索引色"这个概念，libpng 的位深参数管不到它
    let bytes: Uint8Array;
    if (spec.format === 'png8') {
      const { indices, palette } = buildIndexedImage(pixels, width, height, spec.colors ?? 256, spec.dither ?? true);
      bytes = await encodeIndexedPng(indices, palette, width, height);
    } else {
      bytes = await encode(spec.format, pixels, { width, height, quality: spec.quality });
    }
    throwIfAborted(options.signal);

    const name = `${spec.baseName}${suffix}.${extension}`;
    await deps.gateway.save(
      { name, bytes, mimeType: spec.format === 'jpeg' ? 'image/jpeg' : 'image/png' },
      null,
      options,
    );
    results.push({ name, width, height, bytes: bytes.length });
  }
  return results;
}
